import * as React from 'react';

import { InformationBanner, Text } from '@morressier/ts';

import { FaExclamationTriangle } from 'react-icons/fa';

import Spinner from 'components/Spinner';
import VideoPlayerV1 from 'components/VideoPlayer';
import { canDisplayVideoCutterPlayer } from 'components/VideoPlayer/helper';
import { SimpleCard } from 'containers/SessionsList/styles';

import VideoPlayerCutter from './VideoPlayerCutter';

const ProcessingVideo = ({ state }) => (
  <SimpleCard
    color="#1F222C"
    height="100%"
    justifyContent="center"
    alignContent="center"
    alignItems="center"
    cursor="unset"
  >
    {state === 1 ? (
      <Spinner color="white" size="1.5rem" />
    ) : (
      <FaExclamationTriangle color="white" size="1.5rem" />
    )}
    <Text
      size="body1_new"
      as="h2"
      color="white"
      style={{ width: '60%', textAlign: 'center', marginTop: '1rem' }}
    >
      {state === 1
        ? 'This video presentation is currently being processed and will be ready in a few minutes.'
        : 'There was an error processing this video presentation, please try again, or contact support for more information.'}
    </Text>
  </SimpleCard>
);

const Message = (
  <div
    style={{
      padding: '2rem',
      background: 'white',
    }}
  >
    <InformationBanner
      style={{
        background: 'white',
      }}
      type="message"
    >
      This video is currently being processed.
    </InformationBanner>
  </div>
);
interface Props {
  poster: Pick<SessionPoster, 'video_presentation' | 'id' | 'title'>;
  broadcastUrl?: string;
  authUserId?: string;
  onPlay?: (e?: Event) => void;
  logWatchTime?: (props: { watchedFor: number }) => void;
  containerStyles?: React.CSSProperties;
  pictureInPictureToggle?: boolean;
  videoSession: MorressierVideoSession | null;
  archiveStreamUrl?: string;
  organizationId?: string;
}

const Video = React.forwardRef<HTMLVideoElement, Props>((props, ref) => {
  const video = props.broadcastUrl
    ? { playback_url: props.broadcastUrl, processing_state: 2, file_name: '' }
    : props.poster.video_presentation;

  const { videoSession, onPlay } = props;

  // check the backward compatibility with existing poster videos
  const canDisplayPlayer =
    videoSession && canDisplayVideoCutterPlayer(videoSession, props.poster.id);

  const renderVideoPlayer = () => {
    const { poster, authUserId, pictureInPictureToggle = true } = props;
    const muxMetaData = {
      player_name: 'Session View Player',
      viewer_user_id: authUserId || 'Free user',
      video_id: props.poster.id,
      video_title: props.poster.title,
    };

    if (!canDisplayPlayer) {
      if (!video) return null;

      if (video.processing_state === 1) {
        return <ProcessingVideo state={video.processing_state} />;
      }
    }

    if (canDisplayPlayer) {
      return (
        <VideoPlayerCutter
          posterId={poster.id}
          onPlay={onPlay}
          muxMetaData={muxMetaData}
          logWatchTime={props.logWatchTime}
          videoSession={videoSession}
          archiveStreamUrl={props?.archiveStreamUrl}
        />
      );
    }

    if (video?.processing_state === 2 || video?.file_name || props.broadcastUrl) {
      return (
        <VideoPlayerV1
          key={video?.playback_url}
          videoSrc={video?.playback_url || ''}
          muxMetaData={muxMetaData}
          videoJsOptions={{ autoplay: Boolean(props.broadcastUrl) }}
          onEvent={{
            play: props.onPlay ?? (() => null),
          }}
          logWatchTime={props.logWatchTime}
          ref={ref}
          containerStyles={props.containerStyles}
          pictureInPictureToggle={pictureInPictureToggle}
          organizationId={props.organizationId}
        />
      );
    }

    return <ProcessingVideo state={video?.processing_state} />;
  };

  return video?.playback_url || canDisplayPlayer ? renderVideoPlayer() : Message;
});

export default Video;
